import { useCallback, useEffect, useRef, useState } from 'react';
import type { PdfPreviewFocus } from './PdfCanvasViewer';

export type RemoteNavigationPreviewCallback = (
  blob: Blob | null,
  label: string,
  focus?: PdfPreviewFocus | null,
  owner?: string,
) => void;

export interface RemoteNavigationPreview {
  readonly url: string;
  readonly label: string;
  readonly focus: PdfPreviewFocus | null;
}

interface PreviewState extends RemoteNavigationPreview {
  readonly documentId: string;
}

interface ActivePreview {
  readonly documentId: string;
  readonly url: string;
  readonly owner: string | undefined;
}

export function useRemoteNavigationPreview(documentId: string): {
  preview: RemoteNavigationPreview | null;
  onPreview: RemoteNavigationPreviewCallback;
  clearPreview: () => void;
} {
  const [state, setState] = useState<PreviewState | null>(null);
  const activeRef = useRef<ActivePreview | null>(null);
  const docRef = useRef(documentId);
  docRef.current = documentId;
  const nonceRef = useRef(0);
  const mountedRef = useRef(false);

  const nextFocus = useCallback((focus: PdfPreviewFocus | null | undefined): PdfPreviewFocus | null => {
    nonceRef.current += 1;
    return focus ? { ...focus, nonce: nonceRef.current } : null;
  }, []);

  const revokeActive = useCallback(() => {
    const active = activeRef.current;
    activeRef.current = null;
    if (active) URL.revokeObjectURL(active.url);
  }, []);

  // Revoke on document change and on unmount; never set state from a cleanup.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (activeRef.current?.documentId === documentId) revokeActive();
    };
  }, [documentId, revokeActive]);
  useEffect(() => {
    setState((prev) => (prev && prev.documentId !== documentId ? null : prev));
  }, [documentId]);

  const onPreview: RemoteNavigationPreviewCallback = useCallback(
    (blob, label, focus, owner) => {
      if (!mountedRef.current || docRef.current !== documentId) return;
      if (blob) {
        const url = URL.createObjectURL(blob);
        const prev = activeRef.current;
        activeRef.current = { documentId, url, owner };
        if (prev) URL.revokeObjectURL(prev.url);
        setState({ documentId, url, label, focus: nextFocus(focus) });
        return;
      }
      const active = activeRef.current;
      if (!active || (owner !== undefined && owner !== active.owner)) return;
      if (focus !== undefined) {
        const next = nextFocus(focus);
        setState((prev) => (prev && prev.url === active.url ? { ...prev, focus: next } : prev));
      } else {
        revokeActive();
        setState(null);
      }
    },
    [documentId, nextFocus, revokeActive],
  );

  const clearPreview = useCallback(() => {
    if (!mountedRef.current || docRef.current !== documentId) return;
    revokeActive();
    setState(null);
  }, [documentId, revokeActive]);

  const preview = state && state.documentId === documentId
    ? { url: state.url, label: state.label, focus: state.focus }
    : null;

  return { preview, onPreview, clearPreview };
}
